import { Building2, Check, ChevronDown, LogOut, Settings, UserPlus } from "lucide-react";
import { Link, useNavigate } from "@tanstack/react-router";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentOrg } from "@/contexts/OrgContext";
import { cn } from "@/lib/utils";

function OrgInitial({ name, className }: { name: string; className?: string }) {
  const letter = name.trim()[0]?.toUpperCase() ?? "?";
  return (
    <span className={cn("flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-xs font-semibold text-primary-foreground", className)}>
      {letter}
    </span>
  );
}

export function OrgSwitcher({ collapsed }: { collapsed?: boolean }) {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { orgs, currentOrg, currentOrgId, setCurrentOrg } = useCurrentOrg();
  const label = currentOrg?.name ?? "Workspace";
  const canManage = currentOrg && (currentOrg.role === "owner" || currentOrg.role === "admin");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent",
            collapsed && "justify-center px-0"
          )}
          aria-label="Switch workspace"
        >
          <OrgInitial name={label} />
          {!collapsed ? (
            <>
              <span className="min-w-0 truncate text-sm font-semibold">{label}</span>
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            </>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {currentOrg ? (
          <>
            <div className="px-2 py-2">
              <div className="flex items-center gap-2">
                <OrgInitial name={currentOrg.name} className="size-7 text-xs" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{currentOrg.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Organization &middot; {currentOrg.member_count} {currentOrg.member_count === 1 ? "member" : "members"}
                  </p>
                </div>
              </div>
              {canManage ? (
                <div className="mt-2 flex gap-2">
                  <Button variant="outline" size="sm" className="h-7 flex-1 text-xs" asChild>
                    <Link to="/settings/organization"><Settings className="size-3" aria-hidden />Settings</Link>
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 flex-1 text-xs" asChild>
                    <Link to="/settings/organization/members"><UserPlus className="size-3" aria-hidden />Invite members</Link>
                  </Button>
                </div>
              ) : null}
            </div>
            <DropdownMenuSeparator />
          </>
        ) : null}

        <DropdownMenuLabel className="text-xs text-muted-foreground">Workspaces</DropdownMenuLabel>
        {orgs.map((org) => (
          <DropdownMenuItem key={org.id} onClick={() => setCurrentOrg(org.id)}>
            <Building2 className="size-4" aria-hidden />
            <span className={cn("flex-1 truncate", currentOrgId === org.id && "font-medium")}>{org.name}</span>
            {currentOrgId === org.id ? <Check className="size-4" aria-hidden /> : null}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => void navigate({ to: "/settings/organization" })}>
          <Building2 className="size-4" aria-hidden />
          Manage organizations
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            void signOut()
              .then(() => navigate({ to: "/login", replace: true }))
              .catch(() => navigate({ to: "/login", replace: true }));
          }}
        >
          <LogOut className="size-4" aria-hidden />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
